import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { School, Sun, Moon, Monitor } from 'lucide-react';
import { docenteApi, authHelpers } from '../api/docenteApi';
import { applyTheme, getStoredTheme } from '../theme';
import { normalizarCedulaDocente } from '../utils/cedulaDocente';

const LoginDocente = () => {
  const navigate = useNavigate();
  const [cedula, setCedula] = useState('');
  const [email, setEmail] = useState('');  // ✅ NUEVO: Campo para email
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mostrarSelector, setMostrarSelector] = useState(false);
  const [colegios, setColegios] = useState([]);
  const [docenteData, setDocenteData] = useState(null);
  const [seleccionandoColegio, setSeleccionandoColegio] = useState(false);

  // Tema (claro/oscuro/sistema) elegible desde el login. Se guarda en el
  // dispositivo (localStorage) y se mantiene tras iniciar sesión y al cerrarla.
  const [tema, setTema] = useState(getStoredTheme());
  const cambiarTema = (t) => { setTema(t); applyTheme(t); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const cedulaLimpia = normalizarCedulaDocente(cedula);
      if (!cedulaLimpia || !email.trim()) {
        setError('Por favor ingrese su cédula y email');
        return;
      }
      const emailLimpio = email.trim().toLowerCase();  // Normalizar a minúsculas
      
      const response = await docenteApi.login(cedulaLimpia, emailLimpio);

      if (response.requiereSeleccion) {
        setDocenteData(response.docente);
        setColegios(response.colegios || []);
        setMostrarSelector(true);
        return;
      }

      if (response.token) {
        const docenteNormalizado = {
          ...response.docente,
          docenteID: response.docente.id || response.docente.docenteID
        };

        authHelpers.guardarSesion(
          response.token,
          docenteNormalizado,
          response.colegioSeleccionado,
          response.paisCode,
          response.timeZone
        );

        if (response.colegios) {
          authHelpers.guardarColegios(response.colegios);
        }

        if (response.nombreColegio) {
          localStorage.setItem('nombre_colegio', response.nombreColegio);
        }

        navigate('/mis-clases');
      }

    } catch (err) {
      console.error('Error en login:', err);
      setError(err.message || 'Error al iniciar sesión. Verifique su cédula y email.');
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionarColegio = async (colegio) => {
    setError('');
    setSeleccionandoColegio(true);

    try {
      const response = await docenteApi.login(normalizarCedulaDocente(cedula), email.trim(), colegio.colegioID);

      if (response.token) {
        const docenteNormalizado = {
          ...response.docente,
          docenteID: response.docente.id || response.docente.docenteID
        };

        authHelpers.guardarSesion(
          response.token,
          docenteNormalizado,
          response.colegioSeleccionado,
          response.paisCode,
          response.timeZone
        );

        // Guardar lista completa de colegios (la del selector, ya filtrada por activos)
        authHelpers.guardarColegios(response.colegios || colegios);

        if (response.nombreColegio) {
          localStorage.setItem('nombre_colegio', response.nombreColegio);
        }

        navigate('/mis-clases');
      }

    } catch (err) {
      console.error('Error seleccionando colegio:', err);
      setError(err.message || 'Error al seleccionar institución');
    } finally {
      setSeleccionandoColegio(false);
    }
  };

  const volverAlLogin = () => {
    setMostrarSelector(false);
    setColegios([]);
    setDocenteData(null);
    setCedula('');
    setEmail('');  // ✅ NUEVO: Limpiar email
    setError('');
  };

  if (mostrarSelector) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-base-200 safe-top safe-x p-4">
        <div className="card w-full max-w-2xl bg-base-100 shadow-xl">
          <div className="card-body">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-primary">
                Seleccione su Institución
              </h2>
              <p className="text-sm text-base-content/70 mt-2">
                Bienvenido/a, <span className="font-semibold">{docenteData?.nombreCompleto}</span>
              </p>
              <p className="text-xs text-base-content/60 mt-1">
                Trabaja en {colegios.length} {colegios.length === 1 ? 'institución' : 'instituciones'}
              </p>
            </div>

            {error && (
              <div className="alert alert-error mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-3">
              {colegios.map((colegio) => {
                const tieneClases = colegio.tieneMaterias !== false;

                return (
                  <button
                    key={colegio.colegioID}
                    onClick={() => handleSeleccionarColegio(colegio)}
                    disabled={seleccionandoColegio || !tieneClases}
                    className={`card card-compact w-full text-left transition-all hover:shadow-md ${
                      tieneClases 
                        ? 'bg-base-200 hover:bg-base-300' 
                        : 'bg-base-200 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="card-body flex-row items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="avatar placeholder">
                          <div className={`${
                            tieneClases ? 'bg-primary text-primary-content' : 'bg-base-300 text-base-content/40'
                          } rounded-full w-12 flex items-center justify-center`}>
                            <School className="w-6 h-6" strokeWidth={1.75} />
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="font-bold text-base">
                            {colegio.nombreColegio}
                          </h3>
                          <p className="text-xs text-base-content/60">
                            {colegio.sucursal || 'Sede Principal'}
                          </p>
                          {!tieneClases && (
                            <p className="text-xs text-warning mt-1">
                              ⚠️ No tiene materias asignadas en este colegio
                            </p>
                          )}
                        </div>
                      </div>

                      {seleccionandoColegio ? (
                        <span className="loading loading-spinner loading-sm"></span>
                      ) : tieneClases ? (
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-6 w-6 text-base-content/40" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M9 5l7 7-7 7" 
                          />
                        </svg>
                      ) : (
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-6 w-6 text-base-content/20" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="divider text-xs mt-6">¿No es su cédula o email?</div>
            <button 
              onClick={volverAlLogin}
              className="btn btn-ghost btn-sm"
              disabled={seleccionandoColegio}
            >
              ← Volver al login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-base-200 safe-top safe-x">
      <div className="card w-96 bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="text-center mb-6">
            <img
              src="/icon-512.webp"
              alt="Carnet Plus Docente"
              className="w-20 h-20 mx-auto mb-3 rounded-2xl"
            />
            <h1 className="text-3xl font-bold text-primary">Carnet Plus Docente</h1>
            <p className="text-[11px] uppercase tracking-[0.2em] text-base-content/40 mt-1">
              Sistema de Asistencia en Clases
            </p>
            <p className="text-sm text-base-content/70 mt-2">
              Ingresa con tu cédula y correo
            </p>

            {/* Selector de tema (se guarda en el dispositivo) */}
            <div className="flex justify-center gap-1 mt-4">
              {[
                { val: 'light', icon: Sun, label: 'Claro' },
                { val: 'dark', icon: Moon, label: 'Oscuro' },
                { val: 'system', icon: Monitor, label: 'Sistema' },
              ].map(({ val, icon: Icon, label }) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => cambiarTema(val)}
                  className={`btn btn-xs gap-1 ${tema === val ? 'btn-primary' : 'btn-ghost'}`}
                  aria-pressed={tema === val}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit}>

            <div className="form-control mt-4">  {/* ✅ NUEVO: Campo para email */}
              <label className="label">
                <span className="label-text font-semibold">Correo Electrónico</span>
              </label>
              <input
                type="email"
                placeholder="Ingrese su email"
                className="input input-bordered input-primary"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <label className="label">
                <span className="label-text-alt text-base-content/60">
                  Use el email registrado en el sistema
                </span>
              </label>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Número de Cédula</span>
              </label>
              <input
                type="tel"
                placeholder="Ingrese su cédula"
                className="input input-bordered input-primary"
                value={cedula}
                onChange={(e) => setCedula(normalizarCedulaDocente(e.target.value))}
                disabled={loading}
                autoFocus
              />
              <label className="label">
                <span className="label-text-alt text-base-content/60">
                  Escribe tu cédula sin puntos.
                </span>
              </label>
            </div>

            

            {error && (
              <div className="alert alert-error mt-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="form-control mt-6">
              <button 
                type="submit" 
                className={`btn btn-primary ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? 'Ingresando...' : 'Ingresar'}
              </button>
            </div>
          </form>

          <div className="divider text-xs">Información</div>
          <div className="text-center text-xs text-base-content/60">
            <p>Si tiene problemas para ingresar,</p>
            <p>contacte al administrador del sistema</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginDocente;
